import Rollbar from 'rollbar';

import {
  Injectable,
  Inject,
  InjectionToken,
  ErrorHandler
} from '@angular/core';
import {lastValueFrom, take} from "rxjs";
import {environment} from '../../environments/environment';
import {CustomerServiceImpl} from "../services/customer/customer.service.impl";
import {DoorService} from "../services/door/door.service";
import {ScenarioImageService} from "../services/scenario-image/scenario-image.service";
import {UserStorageService} from "../services/user/user.storage.service";

const rollbarConfig: Rollbar.Configuration = {
  enabled: environment.rollbarEnabled,
  accessToken: environment.rollbarClientToken,
  captureUncaught: true,
  verbose: true,
  captureIp: 'anonymize',
  captureUnhandledRejections: true,
  environment: environment.stage,
  reportLevel: 'error',
  ignoredMessages: [
     "ResizeObserver loop completed with undelivered notifications."
  ],
  ignoreDuplicateErrors: true,
  payload: {
    context: {
      customerKey: CustomerServiceImpl.GetCustomerKey(),
    },
    server: {
      brand: 'master',
    },
    client: {
      javascript: {
        code_version: environment.version,
        source_map_enabled: true,
        guess_uncaught_frames: true
      }
    }
  }
};

export const RollbarService = new InjectionToken<Rollbar>('rollbar');

@Injectable()
export class RollbarErrorHandler implements ErrorHandler {
  constructor(@Inject(RollbarService) private rollbar: Rollbar,
              private doorService: DoorService,
              private scenarioService: ScenarioImageService,
              private readonly userStorageService: UserStorageService) {
  }

  handleError(err: any): void {
    const userId = this.userStorageService.getUserId() || 'not-set';

    const applicationContextCustom: {
      doorId: string;
      scenarioId: string;
    } = {
      doorId: 'not-set',
      scenarioId: 'not-set'
    };

    try {
      const currentDoor = this.doorService.getSelectedDoorAtCurrentIndex();
      applicationContextCustom.doorId = currentDoor?.id || 'not-set';
    } catch (e) {
      console.warn('Error getting door', e);
    }

    lastValueFrom(this.scenarioService.getSelected$().pipe(take(1)))
      .then(currentScenario => {
        applicationContextCustom.scenarioId = currentScenario?.id || 'not-set';
      })
      .catch(e => {
        console.warn('Error getting scenario', e);
      })
      .finally(() => {
        try {
          this.rollbar.configure({
            payload: {
              ...rollbarConfig.payload,
              custom: {
                customerKey: CustomerServiceImpl.GetCustomerKey(),
                appUserId: userId,
                ...applicationContextCustom
              },
              person: {
                id: userId
              }
            }
          });
        } catch (e) {
          console.warn('Rollbar config error', e);
        }

        this.rollbar.error(err.originalError || err);
      });
  }
}

export function rollbarFactory(): Rollbar {
  return new Rollbar(rollbarConfig);
}
